import React, { useState, useMemo } from 'react';
import { useHousingUnitsPer1kData } from '@/hooks/useHousingUnitsPer1kData';
import { usePeerHousingExistingData } from '@/hooks/usePeerHousingExistingData';
// There is no peer hook for this metric, so we will only show the selected city.
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shared/card';
import { formatPlaceName, cn } from '@/lib/utils';
import { ComparisonBarChart } from './ComparisonBarChart';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/shared/tooltip";
import { Place } from '@/types';

interface Props {
  place: string | null;
  placeFips: string | null;
  peers: Place[];
  labelFontSize?: number;
}

const HousingUnitsPer1kCard: React.FC<Props> = ({ place, placeFips, peers, labelFontSize }) => {
  const { data: placeData, loading, error } = useHousingUnitsPer1kData(place, placeFips);
  const { data: peerData, loading: peersLoading } = usePeerHousingExistingData(peers);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  const getRankSuffix = (rank: number) => {
    if (rank > 10 && rank < 20) return 'th';
    const lastDigit = rank % 10;
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const chartData = React.useMemo(() => {
    if (!placeData.length) return [];
    
    const mostRecentPlaceData = placeData[placeData.length - 1];
    
    const allCities = peers
      .filter(peer => peer.place_fips.toString() !== placeFips)
      .map(peer => {
        const peerRecords = peerData[peer.place_fips];
        const latestPeerRecord = peerRecords ? peerRecords.sort((a, b) => Number(b.year) - Number(a.year))[0] : null;
        return {
          name: peer.name,
          value: parseFloat((latestPeerRecord?.housing_units_per_1000_residents ?? 0).toFixed(2)),
          isSelected: false,
        };
      });

    if (mostRecentPlaceData) {
      allCities.push({
        name: mostRecentPlaceData.Place,
        value: parseFloat((mostRecentPlaceData.housing_units_per_1000_residents ?? 0).toFixed(2)),
        isSelected: true,
      });
    }
    
    return allCities.sort((a, b) => a.value - b.value);
  }, [placeData, peerData, peers, placeFips]);

  const calculateRank = () => {
    if (!place) return null;
    const index = chartData.findIndex(item => item.isSelected);
    if (index === -1) return null;
    const rank = chartData.length - index;
    return rank > 0 ? `${rank}${getRankSuffix(rank)}` : null;
  };

  const lastYear = placeData.length > 0 ? placeData[placeData.length - 1]?.year : null;

  if (!place || !placeFips) {
    return (
      <Card className="p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Existing Housing Units per Capita</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please select a place to view housing unit data.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="flex flex-col p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Existing Housing Units per Capita</CardTitle>
        </CardHeader>
        <CardContent>
          <div>Loading data...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex flex-col p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Existing Housing per Capita</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="flex flex-col p-4 border border-[#ca0270] relative w-full max-w-[600px]">
        <div className="absolute top-4 left-4 bg-[#908ddb] text-white text-sm font-semibold px-3 py-1 rounded-full">
          Housing Stock
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-4 right-4 bg-[#ca0270] text-white text-sm font-semibold px-3 py-1 rounded-full cursor-help">
              {peersLoading ? 'Calculating...' : `Peer Ranking: ${calculateRank() || 'N/A'}`}
            </div>
          </TooltipTrigger>
          <TooltipContent className="opacity-100 z-50 bg-white text-black" sideOffset={8}>
            <p>Ranked by: Highest (Existing) Housing Units per Capita</p>
          </TooltipContent>
        </Tooltip>
        <CardHeader className="pt-8 pb-0">
          <CardTitle
            className="text-xl lg:text-2xl font-bold"
          >
            Existing Housing Per Capita
          </CardTitle>
          <p className="text-sm text-gray-500 min-h-[4rem] pt-1">Housing units in existing housing stock per 1,000 residents for {lastYear}</p>
        </CardHeader>
        <CardContent className="relative p-0">
          <ComparisonBarChart
            data={chartData}
            yAxisLabel="Housing Units per 1,000 Residents"
            tooltipFormatter={(value) => `${value.toFixed(2)}`}
            selectedColor="#f6b90d"
            height={200}
            margin={{ top: 15, right: 20, bottom: 30, left: 50}}
            labelFontSize={labelFontSize}
          />
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default HousingUnitsPer1kCard; 